import type { ReactNode } from 'react';
import { Section } from 'components/section';
import { Field } from 'components/field';
import { formatAsFeet } from 'components/well-page-content/utils';
import { format0 } from 'utils/number';
import type { AfeHeaderDetail } from '../types';
import styles from './fields-grid.module.scss';

interface AfeScenarioPanelProps {
  detail: AfeHeaderDetail;
}

/**
 * Formats a nullable numeric value with comma separators (0 decimal places).
 * @param {number | null | undefined} value - The number to format
 * @return {string | null} The formatted string, or null if value is null/undefined
 */
const formatOptionalNumber = (
  value: number | null | undefined
): string | null => {
  if (value === null || value === undefined) return null;
  return format0(value);
};

const AfeScenarioPanel = ({ detail }: AfeScenarioPanelProps) => {
  if (detail.afeType !== 'dc_and_e') return null;

  const scenario = detail.afeScenarios[0];
  const lateralLength =
    scenario?.lateralLength ?? detail.wellInformation.plannedLateralLength;
  const tvd = scenario?.tvd ?? detail.wellInformation.plannedTvd;

  const fields: Array<{ label: string; value: ReactNode }> = [
    { label: 'Planned Lateral Length', value: formatAsFeet(lateralLength) },
    { label: 'Planned Total Depth', value: formatAsFeet(tvd) },
    {
      label: 'Days on Well',
      value: formatOptionalNumber(scenario?.daysOnWell),
    },
    {
      label: 'Days to Drill',
      value: formatOptionalNumber(scenario?.daysToDrill),
    },
    {
      label: 'Stage Length (ft)',
      value: formatOptionalNumber(scenario?.stageLength),
    },
    {
      label: 'Proppant Intensity (lbs/ft)',
      value: formatOptionalNumber(scenario?.proppantIntensity),
    },
    {
      label: 'Fluid Intensity (bbl/ft)',
      value: formatOptionalNumber(scenario?.fluidIntensity),
    },
  ];

  return (
    <Section header="Drilling And Completion Scenario">
      <div className={styles.fieldsGrid}>
        {fields.map((field) => (
          <div className={styles.fieldItem} key={field.label}>
            <Field label={field.label} value={field.value} />
          </div>
        ))}
      </div>
    </Section>
  );
};

export { AfeScenarioPanel };
